import { FullscreenExitOutlined, FullscreenOutlined } from '@ant-design/icons';
import { Button, Slider } from 'antd';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player/youtube';

import { PlayIcon } from '~shared/ui/Icons';
import { useWindowInnerWidth } from '~shared/ui/utils';
import { Image } from '~shared/ui';

export interface VideoPlayerProps {
  play: number;
  index: number;
  setPlay: any;
  handlePlayVideo: any;
  src: string;
  title: string;
  icon?: ReactNode;
  startTime?: number;
  endTime?: number;
}

export const VideoPlayer: FC<VideoPlayerProps> = ({
  play,
  index,
  setPlay,
  handlePlayVideo,
  src,
  title,
  startTime = 0,
  endTime = 0,
}) => {
  const windowWidth = useWindowInnerWidth();
  const playerRef = useRef<ReactPlayer>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [playing, setPlaying] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [progress, setProgress] = useState({ played: 0, playedSeconds: 0, duration: 0 });
  const [showControls, setShowControls] = useState(true);

  const playerWidth = windowWidth > 768 ? 400 : windowWidth - 80;
  const playerHeight = playerWidth / 1.7778;

  useEffect(() => {
    if (play === index) {
      setPlaying(true);
      setShowControls(true);
    } else {
      setPlaying(false);
      setShowControls(false);
    }
  }, [play, index]);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const show = () => {
      setShowControls(true);
      clearTimeout(timeout);

      if (playing) {
        timeout = setTimeout(() => {
          setShowControls(false);
        }, 1000);
      }
    };

    const container = containerRef.current;

    if (container) {
      container.addEventListener('mousemove', show);
    }

    if (!playing) {
      setShowControls(true);
    }

    return () => {
      container?.removeEventListener('mousemove', show);
      clearTimeout(timeout);
    };
  }, [playing]);

  useEffect(() => {
    const onFullExit = () => {
      if (!document.fullscreenElement) {
        setFullscreen(false);
      }
    };

    document.addEventListener('fullscreenchange', onFullExit);

    return () => document.removeEventListener('fullscreenchange', onFullExit);
  }, []);

  // const togglePlay = () => setPlaying((p) => !p);

  const toggleFullscreen = async () => {
    const elem = containerRef.current;

    if (!document.fullscreenElement && elem?.requestFullscreen) {
      await elem.requestFullscreen();
      setFullscreen(true);
    } else if (document.exitFullscreen) {
      await document.exitFullscreen();
      setFullscreen(false);
    }
  };

  const handleProgress = (state: any) => {
    setProgress((prev) => ({
      ...prev,
      played: state.played,
      playedSeconds: state.playedSeconds,
    }));

    if (endTime && state.playedSeconds >= endTime) {
      setPlaying(false);
      setPlay(0);
    }
  };

  const handleDuration = (duration: number) => {
    setProgress((prev) => ({ ...prev, duration }));
  };

  const handleSeek = (value: number) => {
    const fraction = value / 100;
    playerRef.current?.seekTo(fraction, 'fraction');
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);

    return `${m}:${s < 10 ? '0' : ''}${s}`;
  };

  return (
    <div
      className="relative w-full max-w-3xl aspect-video bg-black rounded-md overflow-hidden"
      style={{ width: playerWidth, height: playerHeight }}
    >
      {play === index ? (
        <div ref={containerRef} className="relative w-full h-full">
          <ReactPlayer
            ref={playerRef}
            playing={playing}
            controls={false}
            onProgress={handleProgress}
            onDuration={handleDuration}
            onEnded={() => setPlay(0)}
            width="100%"
            height="100%"
            url={`https://www.youtube.com/watch?v=${src}`}
            config={{
              playerVars: {
                start: startTime,
                end: endTime || undefined,
              },
            }}
          />

          {/* Custom Controls */}
          <div
            className={`absolute bottom-0 left-0 right-0 bg-white/60 p-1 transition-opacity duration-300 
              flex gap-2 justify-between items-center ${
                showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
          >
            {/* <Button
              type="text"
              icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
              onClick={togglePlay}
            /> */}
            <Slider
              className="w-full"
              min={0}
              max={100}
              value={progress.played * 100}
              onChange={handleSeek}
              tooltip={{ formatter: null }}
            />
            <span className="whitespace-nowrap text-sm text-gray-700">
              {formatTime(progress.playedSeconds)} / {formatTime(progress.duration)}
            </span>

            <Button
              type="text"
              icon={fullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
              onClick={toggleFullscreen}
            />
          </div>
        </div>
      ) : (
        <Image
          src={`https://img.youtube.com/vi/${src}/0.jpg`}
          width={playerWidth}
          height={playerHeight}
          alt="video thumbnail"
          className="object-cover"
          placeholder
          preview={{
            visible: false,
            maskClassName: 'customize-mask',
            mask: (
              <div
                className="[&_svg]:w-[80px] flex flex-col justify-center items-center"
                onClick={handlePlayVideo}
              >
                <PlayIcon />
                <p className="text-center font-bold text-white">{title}</p>
              </div>
            ),
          }}
        />
      )}
    </div>
  );
};
